import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckDouble } from '@fortawesome/pro-solid-svg-icons';
import { Checkbox } from 'src/components/PulseUI';
import { Tooltip } from 'src/components/PulseUI/Tooltip';
import { REFERRING_DISABLED_FOR_SITES_TOOLTIP } from 'src/components/Analytics/constants';

interface CodeSetItemProps {
  item: any;
  instanceCount: number;
  isMaxedOut: boolean;
  isDisabled: boolean;
  onSelect: (item: any) => void;
  allowMultiple?: boolean;
  onModeToggle?: (item: any, mode: 'treating' | 'sending') => void;
  activeModes?: ('treating' | 'sending')[];
  isLimitReached?: boolean;
  /** Blocks adding this code set in Referring mode (Sites volume type selected). */
  isReferringDisabled?: boolean;
}

/**
 * CodeSetItem component - Renders a single code set item in the dropdown
 *
 * When allowMultiple=false: click to select (original behavior)
 * When allowMultiple=true: inline treating/referring checkboxes aligned to parent grid
 */
export const CodeSetItem: React.FC<CodeSetItemProps> = ({
  item,
  instanceCount,
  isMaxedOut,
  isDisabled,
  onSelect,
  allowMultiple = false,
  onModeToggle,
  activeModes = [],
  isLimitReached = false,
  isReferringDisabled = false,
}) => {
  const isSelected = instanceCount > 0;
  const checkIcon = instanceCount === 2 ? faCheckDouble : faCheck;
  const claimType = !item.type ? 'Encounters' : 'All Claims';
  const isNotClickable = isMaxedOut || isDisabled;
  const shouldShowDisabled = isNotClickable && !isSelected;

  // An already-checked mode stays interactive so it can be unselected, matching
  // how the selection limit is handled below.
  const isReferringChecked = activeModes.includes('sending');
  const isReferringBlockedBySites = !isReferringChecked && isReferringDisabled;

  const handleModeClick = (mode: 'treating' | 'sending') => {
    if (onModeToggle) {
      const isChecked = activeModes.includes(mode);
      if (!isChecked && isLimitReached) return;
      if (mode === 'sending' && isReferringBlockedBySites) return;
      onModeToggle(item, mode);
    }
  };

  if (allowMultiple) {
    return (
      <>
        {/* Label column */}
        <div
          className="pl-9 py-2 flex flex-col justify-start items-start gap-0.5 min-w-0"
          data-testid={`code-set-item-${item.id}`}
        >
          <div
            className={`text-sm font-semibold font-['Inter'] leading-tight ${
              isSelected ? 'text-sky-600' : 'text-black'
            }`}
          >
            {item.label}
          </div>
          <div className="text-tertiary text-sm font-normal font-['Inter'] leading-tight">
            {claimType}
          </div>
        </div>
        {/* Treating checkbox column */}
        <div className="flex items-center justify-center px-1 py-2">
          <Checkbox
            checked={activeModes.includes('treating')}
            onChange={() => handleModeClick('treating')}
            size="sm"
            disabled={!activeModes.includes('treating') && isLimitReached}
            data-testid={`code-set-mode-${item.id}-treating`}
            aria-label={`Treating mode for ${item.label}`}
          />
        </div>
        {/* Referring checkbox column */}
        <div className="flex items-center justify-center pl-1 pr-3 py-2">
          <Tooltip
            content={REFERRING_DISABLED_FOR_SITES_TOOLTIP}
            position="left"
            showOnCondition={isReferringBlockedBySites}
          >
            <Checkbox
              checked={isReferringChecked}
              onChange={() => handleModeClick('sending')}
              size="sm"
              disabled={
                isReferringBlockedBySites ||
                (!isReferringChecked && isLimitReached)
              }
              data-testid={`code-set-mode-${item.id}-sending`}
              aria-label={`Referring mode for ${item.label}`}
            />
          </Tooltip>
        </div>
      </>
    );
  }

  return (
    <div className="w-full" data-testid={`code-set-item-${item.id}`}>
      <div
        className={`self-stretch ${
          shouldShowDisabled
            ? 'cursor-not-allowed opacity-50'
            : isNotClickable
              ? 'cursor-not-allowed'
              : 'cursor-pointer hover:bg-neutral-100'
        }`}
        onClick={() => {
          if (!isNotClickable) {
            onSelect(item);
          }
        }}
      >
        <div className="pl-9 pr-4 py-2 rounded-lg flex items-center justify-between w-full gap-3">
          <div className="flex flex-col justify-start items-start gap-0.5 flex-1 min-w-0">
            <div
              className={`justify-start text-sm font-semibold font-['Inter'] leading-tight ${
                isSelected ? 'text-sky-600' : 'text-black'
              }`}
            >
              {item.label}
            </div>
            <div className="self-stretch justify-start text-tertiary text-sm font-normal font-['Inter'] leading-tight">
              {claimType}
            </div>
          </div>
          <Icon
            icon={checkIcon}
            className={`w-4 h-4 text-sky-600 flex-shrink-0 self-start mt-0.5 ${
              isSelected ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeSetItem;
